
import { supabase } from "@/integrations/supabase/client";

/**
 * Gets a user's phone number directly from the database
 * @param userId The user ID to look up
 * @returns The phone number or null if not found
 */
export async function getUserPhoneNumber(userId: string): Promise<string | null> {
  try {
    // Use type assertion to avoid deep type instantiation
    const { data, error } = await supabase
      .from('users')
      .select('phone_number')
      .eq('user_id', userId)
      .single();
    
    if (error || !data) {
      console.error("Error getting user phone number:", error);
      return null;
    }
    
    return data.phone_number;
  } catch (error) {
    console.error("Error getting user phone number:", error);
    return null;
  }
}

/**
 * Creates or retrieves a special "host" invite record for host notifications
 * @param eventId The event ID
 * @param hostId The host user ID
 * @returns The invite ID or null if creation failed
 */
export async function getOrCreateHostSpecialInvite(eventId: string, hostId: string): Promise<string | null> {
  try {
    // Check for existing invite - use explicit type assertion
    const { data, error } = await supabase
      .from('event_invites')
      .select('invite_id')
      .eq('event_id', eventId)
      .eq('is_host_notification', true)
      .maybeSingle();
    
    if (error) {
      console.error("Error checking for existing host notification invite:", error);
      return null;
    }
    
    // If it exists, return it
    if (data) {
      return data.invite_id;
    }
    
    // Get host name - using separate variable assignments to avoid chaining
    const userResponse = await supabase
      .from('users')
      .select('full_name')
      .eq('user_id', hostId)
      .single();
    
    const userData = userResponse.data;
    const userError = userResponse.error;
    
    if (userError || !userData) {
      console.error("Error getting host name:", userError);
      return null;
    }
    
    // Create a special invite record with simplified query structure
    const insertResponse = await supabase
      .from('event_invites')
      .insert({
        event_id: eventId,
        display_name: `${userData.full_name} (Host)`,
        claimed_by_user_id: hostId,
        claimed: true,
        is_host_notification: true,
        rsvp_status: 'accepted'
      })
      .select('invite_id');
    
    const newInvite = insertResponse.data?.[0];
    const insertError = insertResponse.error;
    
    if (insertError || !newInvite) {
      console.error("Error creating host notification invite:", insertError);
      return null;
    }
    
    return newInvite.invite_id;
  } catch (error) {
    console.error("Error creating host notification invite:", error);
    return null;
  }
}

/**
 * Send SMS notification using the external notification service
 * @param eventId The event ID
 * @param inviteId The invite ID
 * @param notificationType The type of notification
 * @param phoneNumber The phone number to send to
 * @returns Success status
 */
export async function sendSMSNotification(
  eventId: string,
  inviteId: string,
  notificationType: string,
  phoneNumber: string
): Promise<boolean> {
  try {
    // Check if notification has already been sent - using separate query
    const existingCheckResponse = await supabase
      .from('event_notifications')
      .select('*')
      .eq('event_id', eventId)
      .eq('invite_id', inviteId)
      .eq('notification_type', notificationType)
      .eq('status', 'sent')
      .maybeSingle();
    
    if (existingCheckResponse.data) {
      console.log('Notification already sent');
      return true;
    }
    
    // Invoke the Edge Function with simplified structure
    const functionResponse = await supabase.functions.invoke('send-sms', {
      body: {
        eventId,
        inviteId,
        notificationType,
        phone: phoneNumber
      }
    });
    
    if (functionResponse.error) {
      console.error('Error from Edge Function:', functionResponse.error);
      return false;
    }
    
    return functionResponse.data?.success || false;
  } catch (error) {
    console.error('Error sending notification:', error);
    return false;
  }
}
